'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import Cookies from 'js-cookie'
import * as Sentry from '@sentry/nextjs'

// lib files
import { clearProfileFilledAt, getProfile, getSessionDuration, setProfileFilledAt } from '@/lib/profile'
import { ClientSessionInvalid } from '@/lib/errors'
import { clientMetricLogger } from '@/lib/logger-client'

// types
import type { Confirmation, ConfirmedSession } from '@/lib/atoms/profile'
import type { ProfileData } from '@/lib/types/api/profile-data'

// constants
import { SESSION_ID_COOKIE } from '@/lib/constants'

// metrics
import { CLIENT_PROFILE_METRICS } from '@/lib/metric-strings'

interface ResolveOptions {
  profile: ProfileData | null
  setProfile: (profile: ProfileData | null) => void
  setConfirmation: (confirmation: Confirmation) => void
  confirmedSession: ConfirmedSession | null
  setConfirmedSession: (session: ConfirmedSession | null) => void
  maxConfirmedAgeMs: number
  /** A poll tick rather than a page load. Failures are tolerated more quietly. */
  isRevalidation?: boolean
}

function currentSessionId(): string | undefined {
  return Cookies.get(SESSION_ID_COOKIE)
}

function heldProfileStillFresh(sessionId: string, confirmed: ConfirmedSession | null, maxConfirmedAgeMs: number): boolean {
  if (!confirmed) return false
  if (sessionId !== confirmed.sessionId) return false
  return Date.now() - confirmed.confirmedAt < maxConfirmedAgeMs
}

function recordFailure(
  error: unknown,
  { isRevalidation, pid }: { isRevalidation: boolean; pid: string },
) {
  if (error instanceof ClientSessionInvalid) {
    clientMetricLogger(
      CLIENT_PROFILE_METRICS.NAMESPACE,
      `${CLIENT_PROFILE_METRICS.POLL_PROFILE_WIPED} - pid: ${pid}, isRefresh: ${isRevalidation}, sessionDuration: ${getSessionDuration()}, errorType: ${error.constructor?.name}, error: ${error}`,
    )
    return
  }

  Sentry.captureException(error)

  clientMetricLogger(
    CLIENT_PROFILE_METRICS.NAMESPACE,
    `${CLIENT_PROFILE_METRICS.POLL_SERVER_ERROR_PRESERVED} - pid: ${pid}, isRefresh: ${isRevalidation}, error: ${error}`,
  )
}

/**
 * Brings the client's view of who is signed in into step with the server.
 */
export async function resolveProfile({
  profile,
  setProfile,
  setConfirmation,
  confirmedSession,
  setConfirmedSession,
  maxConfirmedAgeMs,
  isRevalidation = false,
}: ResolveOptions): Promise<void> {
  const hasProfile = profile !== null
  const sessionId = currentSessionId()

  // The session cookie is retired on logout and on a 401, so its absence settles
  // the question: there is no session, whatever is still cached. This is the
  // determination pbs_uid used to make, moved onto a value that cannot be
  // unreadable for a live session or readable for a dead one.
  if (!sessionId) {
    if (hasProfile) setProfile(null)
    setConfirmedSession(null)
    setConfirmation('anonymous')
    return
  }

  if (!isRevalidation && hasProfile && heldProfileStillFresh(sessionId, confirmedSession, maxConfirmedAgeMs)) {
    setConfirmation('authenticated')
    return
  }

  // Before any await, so nothing downstream sees a non-terminal state.
  if (hasProfile) setConfirmation('authenticated')

  try {
    const fetched = await getProfile()

    // Profile service can omit is_passport, and getProfile's mvault override is
    // best effort, so an absent flag carries the last known value forward rather
    // than reaching consumers as unknown and being coerced to a speculative no
    // that flips on the next poll (RWEB-9736).
    if (fetched?.personal_data?.is_passport === undefined) {
      fetched.personal_data.is_passport = profile?.personal_data?.is_passport
    }

    setProfile(fetched)
    setConfirmedSession({ sessionId, confirmedAt: Date.now() })
    setConfirmation('authenticated')
    setProfileFilledAt()
  } catch (error) {
    recordFailure(error, { isRevalidation, pid: profile?.profile.pid ?? 'N/A' })

    if (error instanceof ClientSessionInvalid) {
      // An answer, not a failure.
      clearProfileFilledAt()
      setProfile(null)
      setConfirmedSession(null)
      setConfirmation('anonymous')
      return
    }

    // A transient failure must not sign anyone out or blank held data.
    if (!hasProfile) setConfirmation('error')
  }
}
